/**
 * Ship texture-pattern catalog for the in-game Hangar customization
 * panel. PREMIUM-only feature: free pilots see the pattern grid in the
 * Hangar but cannot equip anything other than {@link DEFAULT_SHIP_PATTERN};
 * the server is the authority on this gate (see
 * `validateShipCosmetics`).
 *
 * Patterns are procedural — each is rendered to an off-screen 2D canvas
 * and applied as the hull material's albedo `map`. The same generators
 * live in two places that MUST stay in sync:
 *   - `packages/client/src/game/ship-builder.ts` (in-game render +
 *     hangar 3D preview)
 *   - `packages/server/src/network/admin-ship-preview.ts` (admin
 *     console + public pilot profile, served as a string module)
 *
 * Adding a pattern here flows automatically into the Hangar UI grid,
 * the server's allow-list, and the saved-character schema. The only
 * code that needs an update is the two pattern-texture builders.
 */
/**
 * Default pilot pattern. Every brand-new character starts with this,
 * and free (non-premium) pilots are pinned to it server-side. Picked
 * to match the existing in-game ship look (no overlay) so introducing
 * the customization panel doesn't visually change anything for
 * anyone who hasn't equipped a pattern.
 */
export const DEFAULT_SHIP_PATTERN = 'solid';
/**
 * Render-fallback hull color. Used by the in-game renderer + ship
 * preview ONLY when the race-specific blueprint color cannot be
 * resolved (e.g. NPC with no `raceId`, broken extra bag). The value
 * matches the original pre-customization Terran-ish blue so a
 * fallback render still looks plausible.
 *
 * NOT part of {@link PlayerShipCosmetics}: hull color is fixed per
 * race so that civilizations stay visually distinct in PvP. Pilots
 * customize the *accent* color (and the pattern), never the hull.
 * Kept as a public export so client + server preview modules can
 * share the same fallback constant.
 */
export const DEFAULT_SHIP_PRIMARY_COLOR = 0x18324a;
/**
 * Default accent / wireframe / running-light color. Brand-new (or
 * free) pilots use this — premium pilots can override it via the
 * Hangar Customize panel.
 */
export const DEFAULT_SHIP_SECONDARY_COLOR = 0x44ddff;
/**
 * Default cosmetics bundle. Returned for any character that hasn't
 * customized yet, or whenever the server needs to fall back (free
 * pilot tries to set premium pattern, malformed payload, etc.).
 */
export const DEFAULT_SHIP_COSMETICS = {
    pattern: DEFAULT_SHIP_PATTERN,
    secondaryColor: DEFAULT_SHIP_SECONDARY_COLOR,
};
/**
 * Master pattern catalog. Order is the order shown in the hangar
 * picker grid, with the free `solid` pattern first so the "no
 * customization" option is always reachable for free pilots.
 *
 * IMPORTANT: when adding a new pattern, you MUST also implement the
 * matching texture generator in BOTH `ship-builder.ts` and
 * `admin-ship-preview.ts` or pilots equipping it will see the hull
 * fall back to the default solid look in some places.
 */
export const SHIP_PATTERN_PRESETS = [
    {
        id: 'solid',
        name: 'Solid',
        nameEs: 'Sólido',
        premiumOnly: false,
        description: 'Clean factory hull plating.',
    },
    {
        id: 'stripes',
        name: 'Racing Stripes',
        nameEs: 'Bandas de Carrera',
        premiumOnly: true,
        description: 'Twin accent stripes along the length of the hull.',
    },
    {
        id: 'hex',
        name: 'Hex Plating',
        nameEs: 'Placas Hex',
        premiumOnly: true,
        description: 'Honeycomb plating overlay in the accent color.',
    },
    {
        id: 'camo',
        name: 'Splinter Camo',
        nameEs: 'Camuflaje Astillado',
        premiumOnly: true,
        description: 'Voronoi-style splinter camouflage.',
    },
    {
        id: 'flames',
        name: 'Solar Flames',
        nameEs: 'Llamas Solares',
        premiumOnly: true,
        description: 'Flowing flame gradient streaming back from the bow.',
    },
    {
        id: 'circuit',
        name: 'Circuit Trace',
        nameEs: 'Trazas de Circuito',
        premiumOnly: true,
        description: 'Glowing circuit-trace pattern across the plating.',
    },
];
const PATTERN_BY_ID = new Map(SHIP_PATTERN_PRESETS.map((p) => [p.id, p]));
/**
 * Look up the metadata for a pattern by id. Returns the entry for
 * {@link DEFAULT_SHIP_PATTERN} if the id is unknown — callers should
 * therefore always get a non-null result.
 */
export function getShipPatternInfo(id) {
    return (PATTERN_BY_ID.get(id)
        ?? PATTERN_BY_ID.get(DEFAULT_SHIP_PATTERN));
}
/**
 * `true` when the pattern requires a premium subscription.
 * `solid` is the only freely-available option.
 */
export function isPatternPremiumOnly(id) {
    return getShipPatternInfo(id).premiumOnly;
}
/**
 * Server-side validation of a `SetShipCosmetics` request. Free pilots
 * are allowed to "equip" the default cosmetics bundle (a no-op),
 * which lets a downgraded subscriber stop using their previously-
 * applied premium pattern without the request being silently
 * rejected. Anything beyond that requires premium.
 *
 * Accepts (and silently ignores) a legacy `primaryColor` field on the
 * request payload — older saved character rows persisted hull color
 * before we pinned it per-race, and we don't want a stale field to
 * trip `invalid_color`. Hull color is no longer customizable; only
 * the accent color and the pattern flow through.
 *
 * Returns the cosmetics bundle to actually persist. When the request
 * is invalid, returns the default bundle so the caller can choose to
 * either apply that fallback or surface an error to the user — both
 * paths keep the player out of an "invalid cosmetics" state.
 */
export function validateShipCosmetics(request, hasPremium) {
    if (!request) {
        return { ok: true, cosmetics: DEFAULT_SHIP_COSMETICS };
    }
    const pattern = (request.pattern ?? DEFAULT_SHIP_PATTERN);
    if (!PATTERN_BY_ID.has(pattern)) {
        return { ok: false, cosmetics: DEFAULT_SHIP_COSMETICS, reason: 'invalid_pattern' };
    }
    const secondary = sanitizeHexColor(request.secondaryColor, DEFAULT_SHIP_SECONDARY_COLOR);
    if (secondary === null) {
        return { ok: false, cosmetics: DEFAULT_SHIP_COSMETICS, reason: 'invalid_color' };
    }
    const cosmetics = {
        pattern,
        secondaryColor: secondary,
    };
    // Free pilots may only equip the default bundle. The pattern check
    // is the strict one — the accent color is pure presentation and
    // harmless either way, but we still pin it to default for free
    // pilots so a downgrade reverts the look completely (otherwise a
    // previously-premium pilot could keep their custom paint by
    // re-sending the last cosmetics on every login).
    if (!hasPremium) {
        if (cosmetics.pattern !== DEFAULT_SHIP_PATTERN
            || cosmetics.secondaryColor !== DEFAULT_SHIP_SECONDARY_COLOR) {
            return { ok: false, cosmetics: DEFAULT_SHIP_COSMETICS, reason: 'premium_required' };
        }
    }
    return { ok: true, cosmetics };
}
/**
 * Coerce a wire-payload color to a clean 24-bit unsigned int. Returns
 * `null` for non-finite / negative / out-of-range values so the
 * caller can reject the entire request — silently clamping a
 * malformed color to white would mask client bugs.
 */
function sanitizeHexColor(value, fallback) {
    if (value === undefined || value === null)
        return fallback;
    if (typeof value !== 'number' || !Number.isFinite(value))
        return null;
    const int = Math.floor(value);
    if (int < 0 || int > 0xffffff)
        return null;
    return int;
}
//# sourceMappingURL=ship-patterns.js.map